import { useState, useEffect, useCallback } from 'react';
import { useMountedState } from 'react-use';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { store } from 'CorePackage/MainModule/common/store';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { KnmPublishStatusesEnum } from 'APIPackage/KnmModule/enums/KnmPublishStatusesEnum';
import { KNM_NOT_AVAILABLE_FOR_PUBLISH, PM_NOT_AVAILABLE_FOR_PUBLISH } from 'CorePackage/MainModule/constants/notices';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { fetchKnmPublishById, fetchKnmUnpublishById } from 'APIPackage/KnmModule/services/knmActionsApi';

import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';

export function useKnmPublishById(
  knmSystemType: KnmSystemTypesEnum | undefined,
  initialStatus?: KnmPublishStatusesEnum,
) {
  const isMounted = useMountedState();
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [publishedStatus, setPublishedStatus] = useState<KnmPublishStatusesEnum | null>(initialStatus || null);

  useEffect(() => {
    setPublishedStatus(initialStatus || null);
  }, [initialStatus]);

  const publishKnm = useCallback(
    async (id: number) => {
      try {
        dispatch(coreSetLoading(true));
        const response = await fetchKnmPublishById(id);

        if (isMounted()) {
          setPublishedStatus(response);
        }

        return response;
      } catch (err) {
        const res = err as IResponseError;

        if (res?.message?.includes(MessageTypesEnum.KNM_NOT_AVAILABLE_FOR_PUBLISH)) {
          store.dispatch(
            addNoticeAction({
              renderContent:
                knmSystemType === KnmSystemTypesEnum.PM ? PM_NOT_AVAILABLE_FOR_PUBLISH : KNM_NOT_AVAILABLE_FOR_PUBLISH,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.KNM_NOT_AVAILABLE_FOR_PUBLISH,
            }),
          );
        }
      } finally {
        dispatch(coreSetLoading(false));
      }
    },
    [dispatch, isMounted, knmSystemType],
  );

  const unpublishKnm = useCallback(
    async (id: number) => {
      try {
        dispatch(coreSetLoading(true));
        const response = await fetchKnmUnpublishById(id);

        if (isMounted()) {
          setPublishedStatus(response);
        }

        return response;
      } finally {
        dispatch(coreSetLoading(false));
      }
    },
    [dispatch, isMounted],
  );

  return {
    publishedStatus,
    publishKnm,
    unpublishKnm,
  };
}
